import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import {
  UpsertAggregateBusinessInfo,
  UpsertBaseBusinessInfo,
  UpsertBusinessFromExcelResponse,
} from '@/coreInfrastructureModule/axios/generated/registerBusinessManage/api';
import { UpsertBusinessesRepository } from '@/coreInfrastructureModule/Repositories/UpsertBusinessesRepository';
import { ApiBusinessFreeColumnSettingItem, ISettingRepository } from '@/coreInfrastructureModule/interfaces/ISettingRepository';
import { ExcelRow } from '@/coreDomainsModule/translateExcel';
import { BusinessExcelInfo } from '@/coreDomainsModule/entities/businessExcelInfo';
import { UpsertBusinessError } from '../pages/interfaces/UploadFile';
import { convertDateUnitType } from '@/coreDomainsModule/date';
import { TransformLinkingInput } from '../pages/interfaces/LinkingBusiness';
import { getLeadTimeForSpanText } from '@/appLogic/business/inventorySimulation';
import { commaStringToStringArray } from '@/coreDomainsModule/extends/string';

/**
 * API成功時のインターフェース
 */
export type IUpsertBusinessFromExcelSucceed = UpsertBusinessFromExcelResponse & {
  registerCount: number;
  validateErrors: UpsertBusinessError[];
};

/**
 * エクセルからビジネス情報を登録するユースケース
 * STOPSHIP: バリデーション周りのi18n対応できてない（他にも同様の処理があるので、まとまり次第一括で対応）
 */
export class UpsertBusinessesFromExcelUseCase {
  /**
   * ダミーデータを取得する
   * @param businessInputData - ビジネス情報
   * @param businessExcelInfo - ビジネス情報のエクセル情報
   * @param repository - リポジトリ
   * @param settingRepository - 設定リポジトリ（自由欄設定の取得に使用）
   */
  public async Execute(
    businessInputData: ExcelRow[],
    businessExcelInfo: BusinessExcelInfo,
    repository: UpsertBusinessesRepository,
    settingRepository: ISettingRepository
  ): Promise<INetworkResult<IUpsertBusinessFromExcelSucceed, IError>> {
    const aggregateBusiness: UpsertAggregateBusinessInfo[] = [];
    const baseBusiness: UpsertBaseBusinessInfo[] = [];
    const aggregateBusinessKeyMemo: Record<string, boolean> = {}; // 集計のキーをメモする（不正な値は入らない前提）
    const baseBusinessKeyMemo: Record<string, boolean> = {}; // 取引のキーをメモする（不正な値は入らない前提）
    const upsertBusinessErrors: UpsertBusinessError[] = [];
    const businessFreeColumnSetting = await settingRepository.getBusinessFreeColumnSetting();
    // 設定取得に失敗した場合は自由欄を一切送らず、既存値を保持する
    const aggregateBusinessFreeColumnSettings =
      businessFreeColumnSetting.status.type === 'success' ? businessFreeColumnSetting.status.data.aggregate : [];
    const baseBusinessFreeColumnSettings =
      businessFreeColumnSetting.status.type === 'success' ? businessFreeColumnSetting.status.data.base : [];

    // 取引情報を読み替えつつ取得
    const transformedObject = businessExcelInfo.alternateHeaderInfos.reduce<Record<string, TransformLinkingInput>>(
      (acc, item) => {
        if (item.value || item.unused)
          acc[item.header] = {
            value: item.value,
            unused: item.unused,
          }; // 値が空文字の場合は追加しない(明示的にunusedが指定されている時は別)
        return acc;
      },
      {
        isAggregateBusiness: {
          value: '',
          unused: false,
        },
        warehouseNumber: {
          value: '',
          unused: false,
        },
        warehouse: {
          value: '',
          unused: false,
        },
        supplierNumber: {
          value: '',
          unused: false,
        },
        supplierName: {
          value: '',
          unused: false,
        },
        businessNumber: {
          value: '',
          unused: false,
        },
        businessName: {
          value: '',
          unused: false,
        },
        unit: {
          value: '',
          unused: false,
        },
        package: {
          value: '',
          unused: false,
        },
        customerNumber: {
          value: '',
          unused: false,
        },
        customerName: {
          value: '',
          unused: false,
        },
        salesDestinationNumber: {
          value: '',
          unused: false,
        },
        salesDestinationName: {
          value: '',
          unused: false,
        },
        leadTime: {
          value: '',
          unused: false,
        },
        leadTimeUnit: {
          value: '',
          unused: false,
        },
        inTransitDays: {
          value: '',
          unused: false,
        },
        standardInventoryIndexLower: {
          value: '',
          unused: false,
        },
        standardInventoryIndexUpper: {
          value: '',
          unused: false,
        },
        standardInventoryIndexUnit: {
          value: '',
          unused: false,
        },
        MOQ: {
          value: '',
          unused: false,
        },
        orderPackage: {
          value: '',
          unused: false,
        },
        manufacturer: {
          value: '',
          unused: false,
        },
        endUser: {
          value: '',
          unused: false,
        },
        aggregateBusinessResponsibleUsers: {
          value: '',
          unused: false,
        },
        baseBusinessResponsibleUsers: {
          value: '',
          unused: false,
        },
        salesUnitPrice: {
          value: '',
          unused: false,
        },
        purchaseUnitPrice: {
          value: '',
          unused: false,
        },
      }
    );

    // NOTE: エクセル読み込みの時に、不要な行まで取ってきてしまっているのでここで削除してあげる
    // 以下のキーが全てNullOrEmptyの場合は不必要行としてフィルタリングする
    businessInputData = businessInputData.filter((row) => {
      const isAggregateBusiness = row?.[transformedObject?.isAggregateBusiness.value];
      const warehouseNumber = row?.[transformedObject?.warehouseNumber.value];
      const warehouse = row?.[transformedObject?.warehouse.value];
      const packageValue = row?.[transformedObject?.package.value];
      const businessNumber = row?.[transformedObject?.businessNumber.value];
      const businessName = row?.[transformedObject?.businessName.value];
      const supplierName = row?.[transformedObject?.supplierName.value];
      const supplierNumber = row?.[transformedObject?.supplierNumber.value];
      return !(
        (isAggregateBusiness?.value?.toString() == null || isAggregateBusiness?.value?.toString() == '') &&
        (warehouseNumber?.value?.toString() == null || warehouseNumber?.value?.toString() == '') &&
        (warehouse?.value?.toString() == null || warehouse?.value?.toString() == '') &&
        (packageValue?.value?.toString() == null || packageValue?.value?.toString() == '') &&
        (businessNumber?.value?.toString() == null || businessNumber?.value?.toString() == '') &&
        (businessName?.value?.toString() == null || businessName?.value?.toString() == '') &&
        (supplierName?.value?.toString() == null || supplierName?.value?.toString() == '') &&
        (supplierNumber?.value?.toString() == null || supplierNumber?.value?.toString() == '')
      );
    });

    // NOTE: 集計行→関連行の順に実行したいので並び替え（関連行の存在条件が集計行に依存しているため）
    businessInputData.sort((a, b) => {
      return a?.[transformedObject?.isAggregateBusiness.value]?.value === '集計' ? -1 : 1;
    });

    businessInputData.forEach((row) => {
      const isAggregateBusiness = row?.[transformedObject?.isAggregateBusiness.value] ?? this._generateEmptyRow(false);
      const warehouseNumber = transformedObject?.warehouseNumber.unused
        ? this._generateEmptyRow(true)
        : row?.[transformedObject?.warehouseNumber.value] ?? this._generateEmptyRow(false);
      const warehouse = transformedObject?.warehouse.unused
        ? this._generateEmptyRow(true)
        : row?.[transformedObject?.warehouse.value] ?? this._generateEmptyRow(false);
      const packageValue = transformedObject?.package.unused
        ? this._generateEmptyRow(true, true) // NOTE: 容量は数値でないと登録できないため、'0.01' で登録する
        : row?.[transformedObject?.package.value] ?? this._generateEmptyRow(false);
      const businessNumber = transformedObject?.businessNumber.unused
        ? this._generateEmptyRow(true)
        : row?.[transformedObject?.businessNumber.value] ?? this._generateEmptyRow(false);
      const businessName = transformedObject?.businessName.unused
        ? this._generateEmptyRow(true)
        : row?.[transformedObject?.businessName.value] ?? this._generateEmptyRow(false);
      const supplierName = transformedObject?.supplierName.unused
        ? this._generateEmptyRow(true)
        : row?.[transformedObject?.supplierName.value] ?? this._generateEmptyRow(false);
      const supplierNumber = transformedObject?.supplierNumber.unused
        ? this._generateEmptyRow(true)
        : row?.[transformedObject?.supplierNumber.value] ?? this._generateEmptyRow(false);

      const unit = row?.[transformedObject?.unit.value] ?? this._generateEmptyRow(false);
      const leadTime = row?.[transformedObject?.leadTime.value] ?? this._generateEmptyRow(false);
      const leadTimeUnit = row?.[transformedObject?.leadTimeUnit.value] ?? this._generateEmptyRow(false);
      const inTransitDays = row?.[transformedObject?.inTransitDays.value] ?? this._generateEmptyRow(false);
      const standardInventoryIndexLower = row?.[transformedObject?.standardInventoryIndexLower.value] ?? this._generateEmptyRow(false);
      const standardInventoryIndexUpper = row?.[transformedObject?.standardInventoryIndexUpper.value] ?? this._generateEmptyRow(false);
      const standardInventoryIndexUnit = row?.[transformedObject?.standardInventoryIndexUnit.value] ?? this._generateEmptyRow(false);
      const MOQ = row?.[transformedObject?.MOQ.value] ?? this._generateEmptyRow(false);
      const orderPackage = row?.[transformedObject?.orderPackage.value] ?? this._generateEmptyRow(false);
      const manufacturer = row?.[transformedObject?.manufacturer.value] ?? this._generateEmptyRow(false);
      const endUser = row?.[transformedObject?.endUser.value] ?? this._generateEmptyRow(false);
      const aggregateBusinessResponsibleUsers =
        row?.[transformedObject?.aggregateBusinessResponsibleUsers.value] ?? this._generateEmptyRow(false);
      const baseBusinessResponsibleUsers = row?.[transformedObject?.baseBusinessResponsibleUsers.value] ?? this._generateEmptyRow(false);
      const salesUnitPrice = row?.[transformedObject?.salesUnitPrice.value] ?? this._generateEmptyRow(false);
      const purchaseUnitPrice = row?.[transformedObject?.purchaseUnitPrice.value] ?? this._generateEmptyRow(false);

      // 顧客台帳ID
      // NOTE: とりあえず「顧客台帳ID」でデータを抜き出す
      // TODO: businessMemoはユーザーに設定してもらう項目なので、設定機能ができたらそこで登録したものをキーとして登録できるようにする
      // ↑ 多分複数項目設定して取ることになるんじゃね？
      const userCustomParameters = row?.['顧客台帳ID'] ?? this._generateEmptyRow(false);

      const customerNumber = transformedObject?.customerNumber.unused
        ? this._generateEmptyRow(true)
        : row?.[transformedObject?.customerNumber.value] ?? this._generateEmptyRow(false);
      const customerName = transformedObject?.customerName.unused
        ? this._generateEmptyRow(true)
        : row?.[transformedObject?.customerName.value] ?? this._generateEmptyRow(false);
      const salesDestinationNumber = transformedObject?.salesDestinationNumber.unused
        ? this._generateEmptyRow(true)
        : row?.[transformedObject?.salesDestinationNumber.value] ?? this._generateEmptyRow(false);
      const salesDestinationName = transformedObject?.salesDestinationName.unused
        ? this._generateEmptyRow(true)
        : row?.[transformedObject?.salesDestinationName.value] ?? this._generateEmptyRow(false);

      // 共通バリデーション
      if (
        !(
          warehouseNumber.value?.toString() != null &&
          warehouseNumber.value?.toString() != '' &&
          packageValue.value?.toString() != null &&
          packageValue.value?.toString() != '' &&
          businessNumber.value?.toString() != null &&
          businessNumber.value?.toString() != '' &&
          supplierNumber.value?.toString() != null &&
          supplierNumber.value?.toString() != ''
        )
      ) {
        upsertBusinessErrors.push({
          error: 'RequiredRow',
          cellAddress: Object.values(row)?.[0].address?.toString() ?? '', // 先頭のセルのアドレスを取得（欲しいのは行番号だけなので）
          message: '必須項目が入力されていません',
        });
        return; // このエラーだけ、他のエラーとは違い、エラーがあった場合はその行の処理を終了する
      }

      const aggregateBusinessKey = `${warehouseNumber.value}-${packageValue.value}-${businessNumber.value}-${supplierNumber.value}`;
      const baseBusinessKey = `${warehouseNumber.value}-${packageValue.value}-${businessNumber.value}-${supplierNumber.value}-${customerNumber.value}-${salesDestinationNumber.value}`;

      if (isAggregateBusiness?.value?.toString() === '集計') {
        // 集計取引として扱う（ここは特にバリデーションなし）
        const aggregateBusinessInfo: UpsertAggregateBusinessInfo = {
          warehouseNumber: warehouseNumber.value.toString(),
          package: packageValue.value.toString().parseNumberStringAndRemoveString(),
          businessNumber: businessNumber.value.toString(),
          supplierNumber: supplierNumber.value.toString(),
          businessName: businessName?.value?.toString() ?? '',
          supplierName: supplierName?.value?.toString() ?? '',
          warehouse: warehouse?.value?.toString() ?? '',
          unit: unit?.value?.toString() ?? '',
          leadTime: getLeadTimeForSpanText(Number(leadTime?.value ?? 0), leadTimeUnit?.value?.toString() ?? '').toString() ?? '',
          leadTimeUnit: convertDateUnitType(leadTimeUnit?.value?.toString() ?? ''),
          inTransitDays: inTransitDays?.value != null && inTransitDays?.value !== '' ? Number(inTransitDays.value) : 0,
          stdInventoryIndicesMin: Number(standardInventoryIndexLower?.value),
          stdInventoryIndicesMax: Number(standardInventoryIndexUpper?.value),
          stdInventoryIndicesUnit: convertDateUnitType(standardInventoryIndexUnit?.value?.toString() ?? ''),
          moq: MOQ?.value ? Number(MOQ?.value) : undefined,
          orderPackage: orderPackage?.value?.toString() ?? '',
          manufacturer: manufacturer?.value?.toString() ?? '',
          aggregateBusinessResponsibleUsers: commaStringToStringArray(aggregateBusinessResponsibleUsers?.value?.toString() ?? ''),
          userCustomParameters: userCustomParameters?.value ? { 顧客台帳ID: userCustomParameters.value.toString() } : undefined,
          businessFreeColumnValues: this._createBusinessFreeColumnValues(
            row,
            transformedObject,
            aggregateBusinessFreeColumnSettings,
            'aggregate'
          ),
          purchaseUnitPrice: purchaseUnitPrice?.value?.toString() ?? undefined,
        };
        if (this._validateAggregateBusiness(aggregateBusinessInfo, row, transformedObject, upsertBusinessErrors)) {
          if (aggregateBusinessKeyMemo[aggregateBusinessKey] == null) {
            aggregateBusiness.push(aggregateBusinessInfo); // 問題なかったら追加
            aggregateBusinessKeyMemo[aggregateBusinessKey] = true; // キーをメモ
          } else {
            upsertBusinessErrors.push({
              error: 'DuplicateRow',
              cellAddress: Object.values(row)?.[0].address?.toString() ?? '', // 先頭のセルのアドレスを取得（欲しいのは行番号だけなので）
              message: '同じキーの集計が複数存在しています',
            });
            return;
          }
        }
      } else {
        // 関連取引として扱う
        const baseBusinessInfo: UpsertBaseBusinessInfo = {
          warehouseNumber: warehouseNumber.value.toString(),
          package: packageValue.value.toString().parseNumberStringAndRemoveString(),
          businessNumber: businessNumber.value.toString(),
          supplierNumber: supplierNumber.value.toString(),
          businessName: businessName?.value?.toString() ?? '',
          customerNumber: customerNumber?.value?.toString() ?? '',
          customerName: customerName?.value?.toString() ?? '',
          salesDestinationNumber: salesDestinationNumber?.value?.toString() ?? '',
          salesDestinationName: salesDestinationName?.value?.toString() ?? '',
          supplierName: supplierName?.value?.toString() ?? '',
          warehouse: warehouse?.value?.toString() ?? '',
          endUser: endUser?.value?.toString() ?? '',
          baseBusinessResponsibleUsers: commaStringToStringArray(baseBusinessResponsibleUsers?.value?.toString() ?? ''),
          businessFreeColumnValues: this._createBusinessFreeColumnValues(row, transformedObject, baseBusinessFreeColumnSettings, 'base'),
          salesUnitPrice: salesUnitPrice?.value?.toString() ?? undefined,
        };

        if (aggregateBusinessKeyMemo[aggregateBusinessKey] == null) {
          upsertBusinessErrors.push({
            error: 'NotExistAggregateBusiness',
            cellAddress: Object.values(row)?.[0].address?.toString() ?? '', // 先頭のセルのアドレスを取得（欲しいのは行番号だけなので）
            message: '集計行として登録されていない行が存在します',
          });
          return;
        }

        if (this._validateBaseBusiness(baseBusinessInfo, row, transformedObject, upsertBusinessErrors)) {
          if (baseBusinessKeyMemo[baseBusinessKey] == null) {
            baseBusiness.push(baseBusinessInfo); // 問題なかったら追加
            baseBusinessKeyMemo[baseBusinessKey] = true; // キーをメモ
          } else {
            upsertBusinessErrors.push({
              error: 'DuplicateRow',
              cellAddress: Object.values(row)?.[0].address?.toString() ?? '', // 先頭のセルのアドレスを取得（欲しいのは行番号だけなので）
              message: '同じキーの取引が複数存在しています',
            });
            return;
          }
        }
      }
    });

    // NOTE: 登録できるデータが一つもない場合、API叩かずにvalidateErrorsだけ返す
    if (baseBusiness.length === 0 && aggregateBusiness.length === 0) {
      return {
        status: {
          type: 'success',
          data: {
            statusCode: 500,
            code: 500,
            message: '登録できるデータが存在しません',
            result: {
              registeredBusinessCounts: {
                aggregateBusiness: 0,
                baseBusiness: 0,
              },
            },
            registerCount: 0,
            validateErrors: upsertBusinessErrors,
          },
        },
      };
    }

    const res = await repository.put({
      aggregateBusinesses: aggregateBusiness,
      baseBusinesses: baseBusiness,
    });

    if (res.status.type === 'success') {
      return {
        status: {
          type: 'success',
          data: {
            ...res.status.data,
            registerCount: businessInputData.length,
            validateErrors: upsertBusinessErrors,
          },
        },
      };
    } else {
      return {
        status: {
          type: 'error',
          error: res.status.error,
        },
      };
    }
  }

  /**
   * 集計取引のバリデーション
   * FIXME: 今はunused対象カラムがバリデーションを絶対にpassするので何もしていないが、本当はunusedの場合はバリデーションをスキップするようにしたい
   * @param aggregateBusiness - 集計取引情報
   * @param excelRow - エクセルの行情報
   * @param transformedObject - 読み替え情報
   * @param errors - エラー情報
   */
  private _validateAggregateBusiness(
    aggregateBusiness: UpsertAggregateBusinessInfo,
    excelRow: ExcelRow,
    transformedObject: Record<string, TransformLinkingInput>,
    errors: UpsertBusinessError[]
  ): boolean {
    let output = true;
    const unitArr = ['日', '週', 'ヶ月', 'day', 'week', 'month'];

    if (aggregateBusiness?.warehouse?.toString() == null || aggregateBusiness?.warehouse?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.warehouse.value]?.address?.toString() ?? '',
        message: '置場名が入力されていません',
      });
      output = false;
    } else if (aggregateBusiness.warehouse.length > 255) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.warehouse.value]?.address?.toString() ?? '',
        message: '置場名が255文字を超えています',
      });
      output = false;
    }

    if (aggregateBusiness?.warehouseNumber?.toString() == null || aggregateBusiness?.warehouseNumber?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.warehouseNumber.value]?.address?.toString() ?? '',
        message: '置場コードが入力されていません',
      });
      output = false;
    } else if (aggregateBusiness.warehouseNumber.length > 40) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.warehouseNumber.value]?.address?.toString() ?? '',
        message: '置場コードが40文字を超えています',
      });
      output = false;
    }

    if (aggregateBusiness?.businessName?.toString() == null || aggregateBusiness?.businessName?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.businessName.value]?.address?.toString() ?? '',
        message: '商品名が入力されていません',
      });
      output = false;
    } else if (aggregateBusiness.businessName.length > 132) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.businessName.value]?.address?.toString() ?? '',
        message: '商品名が132文字を超えています',
      });
      output = false;
    }

    if (aggregateBusiness?.businessNumber?.toString() == null || aggregateBusiness?.businessNumber?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.businessNumber.value]?.address?.toString() ?? '',
        message: '商品コードが入力されていません',
      });
      output = false;
    } else if (aggregateBusiness.businessNumber.length > 40) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.businessNumber.value]?.address?.toString() ?? '',
        message: '商品コードが40文字を超えています',
      });
      output = false;
    }

    if (aggregateBusiness?.supplierName?.toString() == null || aggregateBusiness?.supplierName?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.supplierName.value]?.address?.toString() ?? '',
        message: '仕入先名が入力されていません',
      });
      output = false;
    } else if (aggregateBusiness.supplierName.length > 255) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.supplierName.value]?.address?.toString() ?? '',
        message: '仕入先名が255文字を超えています',
      });
      output = false;
    }

    if (aggregateBusiness?.supplierNumber?.toString() == null || aggregateBusiness?.supplierNumber?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.supplierNumber.value]?.address?.toString() ?? '',
        message: '仕入先コードが入力されていません',
      });
      output = false;
    } else if (aggregateBusiness.supplierNumber.length > 40) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.supplierNumber.value]?.address?.toString() ?? '',
        message: '仕入先コードが40文字を超えています',
      });
      output = false;
    }

    if (aggregateBusiness?.package?.toString() == null || aggregateBusiness?.package?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.package.value]?.address?.toString() ?? '',
        message: '容量が入力されていません',
      });
      output = false;
    } else if (isNaN(Number(aggregateBusiness.package))) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.package.value]?.address?.toString() ?? '',
        message: '容量が数字ではありません',
      });
      output = false;
    }

    // NOTE: 単位は必須ではないため、空の場合はエラーにしない
    if (aggregateBusiness?.unit?.toString() != null && aggregateBusiness.unit.length > 6) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.unit.value]?.address?.toString() ?? '',
        message: '単位が6文字を超えています',
      });
      output = false;
    }

    if (aggregateBusiness?.leadTime?.toString() == null || aggregateBusiness?.leadTime?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.leadTime.value]?.address?.toString() ?? '',
        message: 'リードタイムが入力されていません',
      });
      output = false;
    } else if (isNaN(Number(aggregateBusiness.leadTime))) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.leadTime.value]?.address?.toString() ?? '',
        message: 'リードタイムが数字ではありません',
      });
      output = false;
    }

    if (aggregateBusiness?.leadTimeUnit?.toString() == null || aggregateBusiness?.leadTimeUnit?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.leadTimeUnit.value]?.address?.toString() ?? '',
        message: 'リードタイム単位が入力されていません',
      });
      output = false;
    } else if (!unitArr.includes(aggregateBusiness.leadTimeUnit)) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.leadTimeUnit.value]?.address?.toString() ?? '',
        message: 'リードタイム単位が日、週、ヶ月以外です',
      });
      output = false;
    }

    if (aggregateBusiness?.stdInventoryIndicesMin?.toString() == null || aggregateBusiness?.stdInventoryIndicesMin?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.standardInventoryIndexLower.value]?.address?.toString() ?? '',
        message: '標準在庫指数（下限）が入力されていません',
      });
      output = false;
    } else if (isNaN(Number(aggregateBusiness.stdInventoryIndicesMin))) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.standardInventoryIndexLower.value]?.address?.toString() ?? '',
        message: '標準在庫指数（下限）が数字ではありません',
      });
      output = false;
    } else if (!aggregateBusiness.stdInventoryIndicesMin.isDecimal(5, 2)) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.standardInventoryIndexLower.value]?.address?.toString() ?? '',
        message: '標準在庫指数（下限）が整数部分5桁以下、小数点以下2桁までの数字ではありません',
      });
      output = false;
    }

    if (aggregateBusiness?.stdInventoryIndicesMax?.toString() == null || aggregateBusiness?.stdInventoryIndicesMax?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.standardInventoryIndexUpper.value]?.address?.toString() ?? '',
        message: '標準在庫指数（上限）が入力されていません',
      });
      output = false;
    } else if (isNaN(Number(aggregateBusiness.stdInventoryIndicesMax))) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.standardInventoryIndexUpper.value]?.address?.toString() ?? '',
        message: '標準在庫指数（上限）が数字ではありません',
      });
      output = false;
    } else if (!aggregateBusiness.stdInventoryIndicesMax.isDecimal(5, 2)) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.standardInventoryIndexUpper.value]?.address?.toString() ?? '',
        message: '標準在庫指数（上限）が整数部分5桁以下、小数点以下2桁までの数字ではありません',
      });
      output = false;
    }

    if (aggregateBusiness?.stdInventoryIndicesUnit?.toString() == null || aggregateBusiness?.stdInventoryIndicesUnit?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.standardInventoryIndexUnit.value]?.address?.toString() ?? '',
        message: '標準在庫指数単位が入力されていません',
      });
      output = false;
    } else if (!unitArr.includes(aggregateBusiness.stdInventoryIndicesUnit)) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.standardInventoryIndexUnit.value]?.address?.toString() ?? '',
        message: '標準在庫指数単位が日、週、ヶ月以外です',
      });
      output = false;
    }

    // NOTE: MOQは必須ではないため、空の場合はエラーにしない
    if (aggregateBusiness?.moq?.toString() != null && aggregateBusiness?.moq?.toString() !== '') {
      if (isNaN(aggregateBusiness.moq)) {
        errors.push({
          error: 'InvalidValue',
          cellAddress: excelRow?.[transformedObject?.MOQ.value]?.address?.toString() ?? '',
          message: 'MOQが数字ではありません',
        });
        output = false;
      } else if (!aggregateBusiness.moq.isDecimal(18, 6)) {
        errors.push({
          error: 'InvalidValue',
          cellAddress: excelRow?.[transformedObject?.MOQ.value]?.address?.toString() ?? '',
          message: 'MOQが整数部分18桁以下、小数点以下6桁までの数字ではありません',
        });
        output = false;
      }
    }

    // NOTE: manufacturerは必須ではないため、空の場合はエラーにしない
    if (aggregateBusiness.manufacturer && aggregateBusiness.manufacturer.length > 255) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.manufacturer.value]?.address?.toString() ?? '',
        message: '製造社名が255文字を超えています', // STOPSHIP: i18n対応
      });
      output = false;
    }

    // NOTE: aggregateBusinessResponsibleUsersは必須ではないため、空の場合はエラーにしない
    // 仕入担当者は3人までのバリデーション
    if (aggregateBusiness.aggregateBusinessResponsibleUsers && aggregateBusiness.aggregateBusinessResponsibleUsers.length > 3) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.aggregateBusinessResponsibleUsers.value]?.address?.toString() ?? '',
        message: '仕入担当者が3つを超えています', // STOPSHIP: i18n対応
      });
      output = false;
    }

    // 仕入担当者を文字列に変換して文字列長が80文字以下であることを検証する
    if (aggregateBusiness.aggregateBusinessResponsibleUsers) {
      const aggregateBusinessResponsibleUsersString = aggregateBusiness.aggregateBusinessResponsibleUsers.join(',');
      if (aggregateBusinessResponsibleUsersString.length > 80) {
        errors.push({
          error: 'InvalidValue',
          cellAddress: excelRow?.[transformedObject?.aggregateBusinessResponsibleUsers.value]?.address?.toString() ?? '',
          message: '仕入担当者が80文字を超えています', // STOPSHIP: i18n対応
        });
        output = false;
      }
    }

    return output;
  }

  /**
   * 取引のバリデーション
   * @param baseBusiness - 取引情報
   * @param excelRow - エクセルの行情報
   * @param transformedObject - 読み替え情報
   * @param errors - エラー情報
   */
  private _validateBaseBusiness(
    baseBusiness: UpsertBaseBusinessInfo,
    excelRow: ExcelRow,
    transformedObject: Record<string, TransformLinkingInput>,
    errors: UpsertBusinessError[]
  ): boolean {
    let output = true;
    const unitArr = ['日', '週', 'ヶ月', 'day', 'week', 'month'];

    if (baseBusiness?.warehouse?.toString() == null || baseBusiness?.warehouse?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.warehouse.value]?.address?.toString() ?? '',
        message: '置場名が入力されていません',
      });
      output = false;
    } else if (baseBusiness.warehouse.length > 255) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.warehouse.value]?.address?.toString() ?? '',
        message: '置場名が255文字を超えています',
      });
      output = false;
    }

    if (baseBusiness?.warehouseNumber?.toString() == null || baseBusiness?.warehouseNumber?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.warehouseNumber.value]?.address?.toString() ?? '',
        message: '置場コードが入力されていません',
      });
      output = false;
    } else if (baseBusiness.warehouseNumber.length > 40) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.warehouseNumber.value]?.address?.toString() ?? '',
        message: '置場コードが40文字を超えています',
      });
      output = false;
    }

    if (baseBusiness?.businessName?.toString() == null || baseBusiness?.businessName?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.businessName.value]?.address?.toString() ?? '',
        message: '商品名が入力されていません',
      });
      output = false;
    } else if (baseBusiness.businessName.length > 132) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.businessName.value]?.address?.toString() ?? '',
        message: '商品名が132文字を超えています',
      });
      output = false;
    }

    if (baseBusiness?.businessNumber?.toString() == null || baseBusiness?.businessNumber?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.businessNumber.value]?.address?.toString() ?? '',
        message: '商品コードが入力されていません',
      });
      output = false;
    } else if (baseBusiness.businessNumber.length > 40) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.businessNumber.value]?.address?.toString() ?? '',
        message: '商品コードが40文字を超えています',
      });
      output = false;
    }

    if (baseBusiness?.supplierName?.toString() == null || baseBusiness?.supplierName?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.supplierName.value]?.address?.toString() ?? '',
        message: '仕入先名が入力されていません',
      });
      output = false;
    } else if (baseBusiness.supplierName.length > 255) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.supplierName.value]?.address?.toString() ?? '',
        message: '仕入先名が255文字を超えています',
      });
      output = false;
    }

    if (baseBusiness?.supplierNumber?.toString() == null || baseBusiness?.supplierNumber?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.supplierNumber.value]?.address?.toString() ?? '',
        message: '仕入先コードが入力されていません',
      });
      output = false;
    } else if (baseBusiness.supplierNumber.length > 40) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.supplierNumber.value]?.address?.toString() ?? '',
        message: '仕入先コードが40文字を超えています',
      });
      output = false;
    }

    if (baseBusiness?.package?.toString() == null || baseBusiness?.package?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.package.value]?.address?.toString() ?? '',
        message: '容量が入力されていません',
      });
      output = false;
    } else if (isNaN(Number(baseBusiness.package))) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.package.value]?.address?.toString() ?? '',
        message: '容量が数字ではありません',
      });
      output = false;
    }

    if (baseBusiness?.customerName?.toString() == null || baseBusiness?.customerName?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.customerName.value]?.address?.toString() ?? '',
        message: '売上先名が入力されていません',
      });
      output = false;
    } else if (baseBusiness.customerName.length > 255) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.customerName.value]?.address?.toString() ?? '',
        message: '売上先名が255文字を超えています',
      });
      output = false;
    }

    if (baseBusiness?.customerNumber?.toString() == null || baseBusiness?.customerNumber?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.customerNumber.value]?.address?.toString() ?? '',
        message: '売上先コードが入力されていません',
      });
      output = false;
    } else if (baseBusiness.customerNumber.length > 40) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.customerNumber.value]?.address?.toString() ?? '',
        message: '売上先コードが40文字を超えています',
      });
      output = false;
    }

    if (baseBusiness?.salesDestinationName?.toString() == null || baseBusiness?.salesDestinationName?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.salesDestinationName.value]?.address?.toString() ?? '',
        message: '送り先名が入力されていません',
      });
      output = false;
    } else if (baseBusiness.salesDestinationName.length > 255) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.salesDestinationName.value]?.address?.toString() ?? '',
        message: '送り先名が255文字を超えています',
      });
      output = false;
    }

    if (baseBusiness?.salesDestinationNumber?.toString() == null || baseBusiness?.salesDestinationNumber?.toString() === '') {
      errors.push({
        error: 'EmptyKey',
        cellAddress: excelRow?.[transformedObject?.salesDestinationNumber.value]?.address?.toString() ?? '',
        message: '送り先コードが入力されていません',
      });
      output = false;
    } else if (baseBusiness.salesDestinationNumber.length > 40) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.salesDestinationNumber.value]?.address?.toString() ?? '',
        message: '送り先コードが40文字を超えています',
      });
      output = false;
    }

    // NOTE: endUserは必須ではないため、空の場合はエラーにしない
    if (baseBusiness.endUser && baseBusiness.endUser.length > 255) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.endUser.value]?.address?.toString() ?? '',
        message: 'エンドユーザー名が255文字を超えています', // STOPSHIP: i18n対応
      });
      output = false;
    }

    // NOTE: baseBusinessResponsibleUsersは必須ではないため、空の場合はエラーにしない
    // 売上担当者は3人までのバリデーション
    if (baseBusiness.baseBusinessResponsibleUsers && baseBusiness.baseBusinessResponsibleUsers.length > 3) {
      errors.push({
        error: 'InvalidValue',
        cellAddress: excelRow?.[transformedObject?.baseBusinessResponsibleUsers.value]?.address?.toString() ?? '',
        message: '売上担当者が3つを超えています', // STOPSHIP: i18n対応
      });
      output = false;
    }

    // 売上担当者を文字列に変換して文字列長が80文字以下であることを検証する
    if (baseBusiness.baseBusinessResponsibleUsers) {
      const baseBusinessResponsibleUsersString = baseBusiness.baseBusinessResponsibleUsers.join(',');
      if (baseBusinessResponsibleUsersString.length > 80) {
        errors.push({
          error: 'InvalidValue',
          cellAddress: excelRow?.[transformedObject?.baseBusinessResponsibleUsers.value]?.address?.toString() ?? '',
          message: '売上担当者が80文字を超えています', // STOPSHIP: i18n対応
        });
        output = false;
      }
    }

    return output;
  }

  /**
   * 未使用の行を生成する
   * @param unused - 未使用かどうか
   * @param isNumber - 数値かどうか
   */
  private _generateEmptyRow = (
    unused: boolean,
    isNumber = false
  ): {
    value: string | number | undefined;
    address: string | number | undefined;
    fileName: string | number | undefined;
    sheetName: string | number | undefined;
  } => {
    return {
      value: unused ? (isNumber ? 0.01 : '-') : undefined,
      address: undefined,
      fileName: undefined,
      sheetName: undefined,
    };
  };

  /**
   * Excel行から自由欄値を作成する
   * @param row - Excel行
   * @param transformedObject - 列指定情報
   * @param settings - 自由欄設定
   * @param target - 自由欄の対象（集計/関連）。ヘッダーキーの区別と columnNo の採番に使う
   */
  private _createBusinessFreeColumnValues = (
    row: ExcelRow,
    transformedObject: Record<string, TransformLinkingInput>,
    settings: ApiBusinessFreeColumnSettingItem[],
    target: 'aggregate' | 'base'
  ): { columnNo: number; value: string }[] => {
    // unused のカラムは「Excel取込で対象外」なので APIに送らない（既存値保持）
    // unused でなければ、Excelセルが空欄でも APIに送って既存値クリアを許す
    // columnNo は集計・関連それぞれ 1 から採番し、ヘッダーキーは target で区別する
    return settings
      .map((_, index) => {
        const columnNo = index + 1;
        const linkedHeader = transformedObject[`businessFreeColumn-${target}-${columnNo}`];
        if (!linkedHeader || linkedHeader.unused) return null;
        return {
          columnNo,
          value: row?.[linkedHeader.value]?.value?.toString() ?? '',
        };
      })
      .filter((item): item is { columnNo: number; value: string } => item !== null);
  };
}
